import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest, uploadFiles } from '../../lib/api';
import { TableSkeletonLoader } from '../SkeletonLoader';
import { computeFileMd5 } from '../../lib/md5';
import Pagination from '../Pagination';

type AdminFirmware = {
  id: string; version: string; bmcuModel: string; printerModel: string;
  amsSlot?: string | null; variant?: string | null; retractLen: string | null; fileUrl: string | null;
  md5?: string | null; sizeBytes?: number | null;
  releaseNote: string | null; isActive: boolean; createdAt: string;
};

const VALID_SLOTS = ['AMS_A', 'AMS_B', 'AMS_C', 'AMS_D'];

// ─── Shared Styles ────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(16px)',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  border: '1px solid var(--glass-border)'
};

const th: React.CSSProperties = {
  textAlign: 'left', padding: '16px', fontSize: '13px', fontWeight: 600,
  color: '#f8fafc', borderBottom: '1px solid var(--glass-border)', background: 'rgba(255, 255, 255, 0.04)', whiteSpace: 'nowrap'
};

const td: React.CSSProperties = {
  padding: '16px', fontSize: '14px', color: '#cbd5e1', borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
};

function SectionTitle({ title, count }: { title: string, count?: number }) {
  return (
    <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 24px', fontSize: '20px', color: '#f8fafc' }}>
      {title}
      {count !== undefined && (
        <span style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#f8fafc', fontSize: '14px', padding: '4px 10px', borderRadius: '20px', fontWeight: 600, border: '1px solid var(--glass-border)' }}>{count}</span>
      )}
    </h2>
  );
}

function ActionBtn({ children, onClick, danger }: { children: React.ReactNode, onClick: () => void, danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
      background: danger ? 'rgba(225, 29, 72, 0.15)' : 'rgba(255, 255, 255, 0.06)',
      color: danger ? '#f43f5e' : '#f8fafc',
      border: `1px solid ${danger ? 'rgba(225, 29, 72, 0.3)' : 'var(--glass-border)'}`
    }}>
      {children}
    </button>
  );
}

function Badge({ children, color = 'rgba(255,255,255,0.1)', textColor = '#cbd5e1' }: { children: React.ReactNode; color?: string; textColor?: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
      borderRadius: '999px', fontSize: '12px', fontWeight: 600,
      background: color, color: textColor,
    }}>
      {children}
    </span>
  );
}

// Helper: Tự động phân tích tên file .bin chuẩn: [Phiên_bản]-[Chế_độ]-[AMS_Slot]-[Độ_lùi].bin
function parseFirmwareFilename(fileName: string, settings: { printer_models: string[], slots: string[], retract_lengths: string[] }) {
  const rawName = fileName.replace(/\.bin$/i, '').trim();
  
  let parts = rawName.split('-');
  if (parts.length < 4) {
    const underscoreParts = rawName.split('_');
    if (underscoreParts.length >= 4) {
      parts = underscoreParts;
    }
  }

  let detectedVersion = '';
  let detectedPrinter = '';
  let detectedAmsSlot = '';
  let detectedRetract = '';

  if (parts.length >= 4) {
    // 1. Version (VD: 1.0.0 hoặc v1.0.0)
    detectedVersion = parts[0].replace(/^[vV]/, '').trim();

    // 2. Chế độ nạp / Dòng máy (VD: Standard, Soft, HighForce, P1S)
    const modePart = parts[1].toLowerCase().trim();
    if (modePart.includes('soft')) {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('soft')) || 'A1/A1 Mini (Soft Load)';
    } else if (modePart.includes('p1s') || modePart.includes('high')) {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('p1s') || p.toLowerCase().includes('high')) || 'P1S (High Force Load)';
    } else {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('standard') || p.toLowerCase().includes('a1')) || 'A1/A1 Mini (Standard)';
    }

    // 3. Vị trí AMS (VD: AMS_A, AMS_B, AMS_C, AMS_D)
    const slotPart = parts[2].toUpperCase().trim();
    if (slotPart.includes('AMS_A') || slotPart === 'A') {
      detectedAmsSlot = 'AMS_A';
    } else if (slotPart.includes('AMS_B') || slotPart === 'B') {
      detectedAmsSlot = 'AMS_B';
    } else if (slotPart.includes('AMS_C') || slotPart === 'C') {
      detectedAmsSlot = 'AMS_C';
    } else if (slotPart.includes('AMS_D') || slotPart === 'D') {
      detectedAmsSlot = 'AMS_D';
    }

    // 4. Độ lùi sợi (VD: 0.10m, 0.20m, 0.095m)
    const retractClean = parts[3].replace(/[mf]$/i, '').trim();
    const matched = settings.retract_lengths.find(r => r.replace(/[mf]$/i, '') === retractClean);
    detectedRetract = matched || retractClean;
  } else {
    // Fallback: Tìm kiếm theo từ khóa
    const nameLower = rawName.toLowerCase();
    for (const slot of VALID_SLOTS) {
      if (nameLower.includes(slot.toLowerCase()) || nameLower.includes(slot.toLowerCase().replace('_', ''))) {
        detectedAmsSlot = slot;
        break;
      }
    }
    if (nameLower.includes('soft')) {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('soft')) || 'A1/A1 Mini (Soft Load)';
    } else if (nameLower.includes('p1s') || nameLower.includes('high')) {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('p1s') || p.toLowerCase().includes('high')) || 'P1S (High Force Load)';
    } else if (nameLower.includes('standard') || nameLower.includes('a1')) {
      detectedPrinter = settings.printer_models.find(p => p.toLowerCase().includes('standard') || p.toLowerCase().includes('a1')) || 'A1/A1 Mini (Standard)';
    }
    for (const r of settings.retract_lengths) {
      if (nameLower.includes(r.replace('.', '')) || nameLower.includes(r)) {
        detectedRetract = r;
        break;
      }
    }
    const verMatch = rawName.match(/[vV]?(\d+\.\d+(?:\.\d+)?)/);
    if (verMatch) {
      detectedVersion = verMatch[1];
    }
  }

  return { detectedVersion, detectedPrinter, detectedAmsSlot, detectedRetract };
}

function FirmwareActionDropdown({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 140;
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(10, rect.right + window.scrollX - menuWidth),
      });
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: open ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: open ? '#f8fafc' : '#cbd5e1',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: 1,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = '#fff';
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#cbd5e1';
          }
        }}
        title="Thao tác"
      >
        ⋮
      </button>

      {open &&
        createPortal(
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: menuPos.top + 'px',
              left: menuPos.left + 'px',
              zIndex: 99999,
              minWidth: '140px',
              background: '#0f172a',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: '10px',
              padding: '6px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.7), 0 8px 10px -6px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#e2e8f0',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>✏️</span>
              <span>Chỉnh sửa</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#f87171',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
                marginTop: '2px',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>🗑️</span>
              <span>Xoá</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}



export default function FirmwaresTab({ showToast }: { showToast: (msg: string, type?: 'info'|'error'|'warning') => void }) {
  const [firmwares, setFirmwares] = useState<AdminFirmware[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    version: '', bmcuModel: '', printerModel: '', amsSlot: 'AMS_A', retractLen: '',
    fileUrl: '', md5: '', sizeBytes: undefined as number | undefined, releaseNote: '', isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detectedBadge, setDetectedBadge] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminFirmware | null>(null);

  const [settings, setSettings] = useState<{ bmcu_models: string[], printer_models: string[], slots: string[], retract_lengths: string[] }>({
    bmcu_models: [], printer_models: [], slots: [],
    retract_lengths: ['0.10', '0.20', '0.25', '0.30', '0.35', '0.40', '0.45', '0.50', '0.55', '0.60', '0.65', '0.70', '0.75', '0.80', '0.85', '0.90']
  });

  // Lock background scroll when modal is active
  useEffect(() => {
    if (showForm || confirmDelete) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showForm, confirmDelete]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [fwRes, settingsRes] = await Promise.all([
        apiRequest<{ firmwares: AdminFirmware[] }>('/admin/firmwares', undefined, true),
        apiRequest<{ settings: any }>('/admin/settings', undefined, true)
      ]);
      setFirmwares(fwRes.firmwares);

      const s = settingsRes.settings;
      setSettings({
        bmcu_models: s?.bmcu_models || ['BMCU 370 Type C'],
        printer_models: s?.printer_models || ['A1/A1 Mini (Standard)', 'A1/A1 Mini (Soft Load)', 'P1S (High Force Load)'],
        slots: VALID_SLOTS,
        retract_lengths: s?.retract_lengths || ['0.10', '0.20', '0.25', '0.30', '0.35', '0.40', '0.45', '0.50', '0.55', '0.60', '0.65', '0.70', '0.75', '0.80', '0.85', '0.90'],
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploading(true);
    try {
      // Calculate MD5 and size directly in the client browser
      const { md5, sizeBytes } = await computeFileMd5(file);
      const urls = await uploadFiles([file]);
      if (urls.length > 0) {
        const { detectedVersion, detectedPrinter, detectedAmsSlot, detectedRetract } = parseFirmwareFilename(file.name, settings);

        setForm(prev => ({
          ...prev,
          fileUrl: urls[0],
          md5,
          sizeBytes,
          version: detectedVersion || prev.version,
          printerModel: detectedPrinter || prev.printerModel,
          amsSlot: detectedAmsSlot || prev.amsSlot,
          retractLen: detectedRetract || prev.retractLen,
        }));

        const items = [];
        if (detectedVersion) items.push(`Phiên bản: v${detectedVersion}`);
        if (detectedPrinter) items.push(`Chế độ: ${detectedPrinter}`);
        if (detectedAmsSlot) items.push(`Slot: ${detectedAmsSlot}`);
        if (detectedRetract) items.push(`Độ lùi: ${detectedRetract}m`);
        items.push(`Dung lượng: ${(sizeBytes / 1024).toFixed(1)} KB`);
        items.push(`MD5: ${md5.substring(0, 8)}...`);

        if (items.length > 0) {
          setDetectedBadge(items.join(' • '));
        }
      }
    } catch (error) {
      alert('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amsSlot: form.amsSlot || undefined,
        retractLen: form.retractLen || undefined,
        fileUrl: form.fileUrl || undefined,
        md5: form.md5 ? form.md5.toLowerCase().trim() : undefined,
        sizeBytes: form.sizeBytes !== undefined ? form.sizeBytes : undefined,
        releaseNote: form.releaseNote || undefined,
      };
      if (editingId) {
        const response = await apiRequest<{ firmware: AdminFirmware }>(`/admin/firmwares/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        setFirmwares(prev => prev.map(firmware => firmware.id === editingId ? response.firmware : firmware));
        showToast('Đã cập nhật firmware thành công', 'info');
      } else {
        const response = await apiRequest<{ firmware: AdminFirmware }>('/admin/firmwares', { method: 'POST', body: JSON.stringify(payload) }, true);
        setFirmwares(prev => [response.firmware, ...prev]);
        showToast('Đã thêm firmware thành công', 'info');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ version: '', bmcuModel: '', printerModel: '', amsSlot: 'AMS_A', retractLen: '', fileUrl: '', md5: '', sizeBytes: undefined, releaseNote: '', isActive: true });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi lưu firmware', 'error');
    } finally {
      setSaving(false);
    }
  }

  function openAdd() {
    setEditingId(null);
    setDetectedBadge(null);
    setForm({
      version: '',
      bmcuModel: settings.bmcu_models[0] || 'BMCU 370 Type C',
      printerModel: settings.printer_models[0] || 'A1/A1 Mini (Standard)',
      amsSlot: 'AMS_A',
      retractLen: '',
      fileUrl: '',
      md5: '',
      sizeBytes: undefined,
      releaseNote: '',
      isActive: true
    });
    setShowForm(true);
  }

  function openEdit(fw: AdminFirmware) {
    setEditingId(fw.id);
    setDetectedBadge(null);
    setForm({
      version: fw.version,
      bmcuModel: fw.bmcuModel,
      printerModel: fw.printerModel,
      amsSlot: fw.amsSlot || fw.variant || 'AMS_A',
      retractLen: fw.retractLen || '',
      fileUrl: fw.fileUrl || '',
      md5: fw.md5 || '',
      sizeBytes: fw.sizeBytes ?? undefined,
      releaseNote: fw.releaseNote || '',
      isActive: fw.isActive
    });
    setShowForm(true);
  }

  async function toggleActive(fw: AdminFirmware) {
    try {
      const response = await apiRequest<{ firmware: AdminFirmware }>(`/admin/firmwares/${fw.id}`, { method: 'PUT', body: JSON.stringify({ isActive: !fw.isActive }) }, true);
      setFirmwares(prev => prev.map(firmware => firmware.id === fw.id ? response.firmware : firmware));
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi cập nhật', 'error');
    }
  }

  async function performDelete() {
    if (!confirmDelete) return;
    const target = confirmDelete;
    setConfirmDelete(null);
    setFirmwares(prev => prev.filter(f => f.id !== target.id));
    try {
      await apiRequest(`/admin/firmwares/${target.id}`, { method: 'DELETE' }, true);
      showToast(`Đã xoá firmware v${target.version}`, 'info');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi xoá firmware', 'error');
      await loadData();
    }
  }

  const filteredFirmwares = firmwares.filter(fw => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      fw.version.toLowerCase().includes(q) ||
      fw.bmcuModel.toLowerCase().includes(q) ||
      fw.printerModel.toLowerCase().includes(q) ||
      (fw.amsSlot && fw.amsSlot.toLowerCase().includes(q)) ||
      (fw.variant && fw.variant.toLowerCase().includes(q)) ||
      (fw.retractLen && fw.retractLen.toLowerCase().includes(q)) ||
      (fw.md5 && fw.md5.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(filteredFirmwares.length / pageSize) || 1;
  const paginatedFirmwares = filteredFirmwares.slice((page - 1) * pageSize, page * pageSize);

  const inputStyle: React.CSSProperties = {
    padding: '10px 14px', borderRadius: '10px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid var(--glass-border)',
    color: '#f8fafc',
    fontSize: '14px', width: '100%', boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <SectionTitle title="Quản lý Firmware" count={firmwares.length} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍 Tìm Version, Chế độ, Slot, MD5..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid var(--glass-border)',
                fontSize: '13px',
                width: '270px',
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#131823',
                color: '#f8fafc',
                outline: 'none',
              }}
            />
            <button type="button" onClick={openAdd} style={{
              padding: '10px 18px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #e11d48, #be123c)',
              color: 'white', border: 'none', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)',
              whiteSpace: 'nowrap',
            }}>
              + Thêm Firmware
            </button>
          </div>
        </div>

        {/* Form Modal - Render via React Portal to document.body */}
        {showForm && createPortal(
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 100000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            padding: '20px'
          }}>
            <div style={{
              background: '#0e121a', borderRadius: '16px', padding: '32px',
              border: '1px solid var(--glass-border)',
              width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
              color: '#f8fafc', position: 'relative'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>{editingId ? 'Sửa Firmware' : 'Thêm Firmware mới'}</h3>
                <button type="button" onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
              </div>
              <form onSubmit={handleSubmitForm}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Phiên bản <span style={{ color: '#ef4444' }}>*</span></label>
                    <input required style={inputStyle} value={form.version} placeholder="VD: 10.01" onChange={e => setForm({ ...form, version: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Model BMCU <span style={{ color: '#ef4444' }}>*</span></label>
                    <select required style={inputStyle} value={form.bmcuModel} onChange={e => setForm({ ...form, bmcuModel: e.target.value })}>
                      <option value="" style={{ background: '#0f172a', color: '#fff' }}>Chọn Model BMCU...</option>
                      {settings.bmcu_models.map(m => <option key={m} value={m} style={{ background: '#0f172a', color: '#fff' }}>{m}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Tương thích / Chế độ nạp (Load Mode) <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '8px 0' }}>
                      {settings.printer_models.map(m => (
                        <label key={m} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', color: '#e2e8f0' }}>
                          <input type="radio" name="printerModel" checked={form.printerModel === m} onChange={() => setForm({ ...form, printerModel: m })} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#e11d48' }} />
                          <span style={{ fontWeight: form.printerModel === m ? 700 : 500 }}>{m}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Vị trí AMS (AMS Slot) <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', padding: '8px 0' }}>
                      {VALID_SLOTS.map(s => (
                        <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#e2e8f0' }}>
                          <input type="radio" name="amsSlot" checked={form.amsSlot === s} onChange={() => setForm({ ...form, amsSlot: s })} style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#e11d48' }} />
                          <span style={{ fontWeight: form.amsSlot === s ? 700 : 500 }}>{s}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Độ lùi sợi / Retract Length</label>
                    <select style={inputStyle} value={form.retractLen} onChange={e => setForm({ ...form, retractLen: e.target.value })}>
                      <option value="" style={{ background: '#0f172a', color: '#fff' }}>-- Mặc định --</option>
                      {settings.retract_lengths.map(r => <option key={r} value={r} style={{ background: '#0f172a', color: '#fff' }}>{r}m</option>)}
                    </select>
                    <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Chiều dài lùi sợi (đơn vị m) cho bản AMS này.</span>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>File .bin</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input style={{ ...inputStyle, flex: 1 }} value={form.fileUrl} placeholder="URL file .bin" onChange={e => setForm({ ...form, fileUrl: e.target.value })} />
                      <label style={{
                        padding: '0 16px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', color: '#f8fafc', border: '1px solid var(--glass-border)',
                        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center'
                      }}>
                        {uploading ? '⏳ Đang tải...' : '📁 Upload'}
                        <input type="file" accept=".bin" style={{ display: 'none' }} onChange={handleFileChange} disabled={uploading} />
                      </label>
                    </div>
                    {detectedBadge && (
                      <div style={{
                        marginTop: '8px', padding: '8px 12px', borderRadius: '8px',
                        background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.3)',
                        color: '#4ade80', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px'
                      }}>
                        <span>✓</span>
                        <span>Đã tự động nhận diện: {detectedBadge}</span>
                      </div>
                    )}
                    <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                      💡 Mẹo: Đặt tên file theo cú pháp <code>10.01-Standard-AMS_B-0.10m.bin</code> để tự động điền các trường trên.
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', gridColumn: '1 / -1' }}>
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>
                        Mã MD5 Checksum (Toàn vẹn dữ liệu)
                      </label>
                      <input
                        style={inputStyle}
                        value={form.md5}
                        placeholder="Tự động tính khi chọn file hoặc nhập thủ công..."
                        onChange={e => setForm({ ...form, md5: e.target.value })}
                      />
                      <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                        Dùng để kiểm tra tính toàn vẹn trước khi nạp vào vi điều khiển.
                      </span>
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>
                        Dung lượng (Bytes)
                      </label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={form.sizeBytes ?? ''}
                        placeholder="VD: 51200"
                        onChange={e => setForm({ ...form, sizeBytes: e.target.value ? parseInt(e.target.value) : undefined })}
                      />
                      <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                        {form.sizeBytes ? `≈ ${(form.sizeBytes / 1024).toFixed(1)} KB` : 'Tự động tính khi chọn file'}
                      </span>
                    </div>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px', color: '#cbd5e1' }}>Ghi chú (Release Note)</label>
                    <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.releaseNote} placeholder="Sửa lỗi gì, tính năng gì mới..." onChange={e => setForm({ ...form, releaseNote: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', color: '#f8fafc' }}>
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#e11d48' }} />
                      Kích hoạt bản cập nhật này (Public)
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                  <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', color: '#cbd5e1', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: 'pointer' }}>Hủy</button>
                  <button type="submit" disabled={saving || uploading} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: 'linear-gradient(135deg, #e11d48, #be123c)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)' }}>{saving ? 'Đang lưu...' : 'Lưu lại'}</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}



        {loading ? <TableSkeletonLoader rows={5} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Firmware & Thiết bị</th>
                  <th style={th}>Cấu hình Slot & Retract</th>
                  <th style={th}>Tệp & MD5</th>
                  <th style={th}>Trạng thái</th>
                  <th style={{ ...th, textAlign: 'center' }}>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFirmwares.map(fw => (
                  <tr key={fw.id}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          background: 'rgba(56, 189, 248, 0.15)',
                          color: '#38bdf8',
                          border: '1px solid rgba(56, 189, 248, 0.3)',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700
                        }}>
                          v{fw.version}
                        </span>
                        <span style={{ fontWeight: 600, color: '#f8fafc', fontSize: '13px' }}>
                          {fw.bmcuModel}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        <span style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          color: '#cbd5e1',
                          border: '1px solid rgba(255, 255, 255, 0.08)'
                        }}>
                          {fw.printerModel}
                        </span>
                      </div>
                    </td>

                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          background: 'rgba(192, 132, 252, 0.15)',
                          color: '#c084fc',
                          border: '1px solid rgba(192, 132, 252, 0.3)',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {fw.amsSlot || fw.variant || 'AMS_AUTO'}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#fbbf24', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {fw.retractLen ? `Retract: ${fw.retractLen}m` : <span style={{ color: '#64748b' }}>Retract: Mặc định</span>}
                      </div>
                    </td>

                    <td style={td}>
                      <div style={{ fontSize: '12px', color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                        {fw.sizeBytes ? `${(fw.sizeBytes / 1024).toFixed(1)} KB` : '—'}
                      </div>
                      <div style={{ marginTop: '3px', whiteSpace: 'nowrap' }}>
                        {fw.md5 ? (
                          <span
                            title={`Mã MD5: ${fw.md5} (Nhấp để sao chép)`}
                            onClick={() => {
                              navigator.clipboard.writeText(fw.md5!);
                              showToast(`Đã sao chép MD5: ${fw.md5}`);
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontFamily: 'monospace', fontSize: '11px', padding: '1px 6px',
                              borderRadius: '4px', background: 'rgba(56, 189, 248, 0.1)',
                              color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.25)',
                              cursor: 'pointer'
                            }}
                          >
                            <span>🔒</span>
                            <span>{fw.md5.substring(0, 8)}...</span>
                          </span>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '11px' }}>Chưa có MD5</span>
                        )}
                      </div>
                    </td>

                    <td style={td}>
                      <button type="button" onClick={() => toggleActive(fw)} style={{
                        border: 'none', background: 'none', cursor: 'pointer', padding: 0
                      }}>
                        <Badge color={fw.isActive ? '#dcfce7' : '#f1f5f9'} textColor={fw.isActive ? '#166534' : '#64748b'}>
                          {fw.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </button>
                    </td>

                    <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <FirmwareActionDropdown
                        onEdit={() => openEdit(fw)}
                        onDelete={() => setConfirmDelete(fw)}
                      />
                    </td>
                  </tr>
                ))}
                {paginatedFirmwares.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '32px' }}>Không tìm thấy bản Firmware nào</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filteredFirmwares.length}
          pageSize={pageSize}
          onPageChange={setPage}
          label="firmware"
        />
      </div>

      {/* Confirm Delete Modal - Render via React Portal */}
      {confirmDelete && createPortal(
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)', padding: '20px'
        }}>
          <div style={{
            background: '#0e121a', borderRadius: '16px', padding: '32px',
            border: '1px solid var(--glass-border)',
            width: '100%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            color: '#f8fafc'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Xác nhận xoá</h3>
              <button type="button" onClick={() => setConfirmDelete(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5, marginBottom: '32px' }}>
              Xoá bản Firmware <strong>v{confirmDelete.version}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)',
                color: '#cbd5e1', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: 'pointer',
              }}>Hủy</button>
              <button type="button" onClick={performDelete} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: '#dc2626',
                color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
              }}>Xác nhận xoá</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

